import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import type { ContentfulSysProps } from '../../types/contentful';
import type {
  ExperimentsByIdsLinkingToSlugsData,
  PagesWithSubOrSideNavData,
} from './slugNavigationQuery';
import { experimentsByIdsLinkingToSlugs, pagesWithSubOrSideNavQuery } from './slugNavigationQuery';

interface SlugToNavMap {
  sideNavMap: Map<string, ContentfulSysProps>;
  subNavMap: Map<string, ContentfulSysProps>;
}

/**
 * Looks up maps from /slug to the ids of SideNavigation and SubNavigation.
 *
 * Works even through experiments.
 *
 * Done as a reverse lookup to avoid loading ALL slugs which is slow on sites like forbusiness.
 */
export function useSlugToNavMap(): SlugToNavMap {
  const sideNavMap = new Map<string, ContentfulSysProps>();
  const subNavMap = new Map<string, ContentfulSysProps>();

  const experimentIdToSideNav = new Map<string, ContentfulSysProps>();
  const experimentIdToSubNav = new Map<string, ContentfulSysProps>();

  // STEP 1: Find all pages with navigation bars (Sub or Side).

  const { data: pagesWithNav } = useContentfulQuery<PagesWithSubOrSideNavData>(
    pagesWithSubOrSideNavQuery
  );

  for (const page of pagesWithNav?.pageCollection.items ?? []) {
    for (const parent of page.linkedFrom.entryCollection.items) {
      // if a page is referenced from an archive slug it will shop up as undefined in linkedFrom
      // so we need to check here so staging sites don't break. doesn't happen when preview=false
      if (!parent) continue;

      switch (parent.__typename) {
        case 'Experiment': {
          page.sideNavigation && experimentIdToSideNav.set(parent.sys.id, page.sideNavigation);
          page.subNavigation && experimentIdToSubNav.set(parent.sys.id, page.subNavigation);
          break;
        }

        case 'Slug': {
          parent.slug && page.sideNavigation && sideNavMap.set(parent.slug, page.sideNavigation);
          parent.slug && page.subNavigation && subNavMap.set(parent.slug, page.subNavigation);
          break;
        }
      }
    }
  }

  // STEP 2: Lookup slug strings for experiments (if any)

  const experimentIds = new Set<string>([
    ...experimentIdToSideNav.keys(),
    ...experimentIdToSubNav.keys(),
  ]);

  const { data: slugsFromExperiments } = useContentfulQuery<
    ExperimentsByIdsLinkingToSlugsData,
    { ids: string[] }
  >(experimentsByIdsLinkingToSlugs, {
    variables: { ids: [...experimentIds] },
    skip: experimentIds.size === 0,
  });

  for (const experiment of slugsFromExperiments?.experimentCollection.items ?? []) {
    for (const link of experiment.linkedFrom.entryCollection.items) {
      if (link.__typename === 'Slug' && link.slug) {
        const sideNav = experimentIdToSideNav.get(experiment.sys.id);
        sideNav && sideNavMap.set(link.slug, sideNav);
        const subNav = experimentIdToSubNav.get(experiment.sys.id);
        subNav && subNavMap.set(link.slug, subNav);
      }
    }
  }

  return { sideNavMap, subNavMap };
}
